import { getOktaAuth, isOktaEnabled } from "../auth/oktaConfig";

// Single fetch wrapper that:
// - adds Authorization: Bearer <accessToken> when Okta is configured
// - sets JSON content-type for write requests
// - throws on non-2xx with the parsed JSON detail (or text body)
// Every api/* module funnels through this so auth + error parsing
// stay consistent.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Best-effort human-readable extraction from an unknown error value.
// Handles the FastAPI/operator response shape (`{ detail: string }` or
// `{ detail: { message: string, ...} }`) and falls back to status+body
// for ApiError or `String(err)` for everything else.
export function extractErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | string | undefined;
    const detail =
      typeof body === "object" && body !== null && "detail" in body
        ? (body as { detail: unknown }).detail
        : body;
    if (typeof detail === "string") return detail;
    if (
      typeof detail === "object" &&
      detail !== null &&
      "message" in detail &&
      typeof (detail as { message: unknown }).message === "string"
    ) {
      return (detail as { message: string }).message;
    }
    return fallback ?? `${err.status}: ${JSON.stringify(err.body)}`;
  }
  return fallback ?? String(err);
}

export async function fetchWithAuth(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  const oktaAuth = getOktaAuth();
  if (isOktaEnabled() && oktaAuth) {
    const token = await oktaAuth.getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // Bypass the browser HTTP cache for API calls. Without this, a 200
  // response (including the SPA shell that the portal serves as a
  // catch-all when a route is missing) gets cached by etag/last-modified
  // and a later restart of the portal — which would now correctly
  // route the same URL to JSON — keeps surfacing the stale HTML. Cost
  // is negligible: API calls are small and rarely identical anyway.
  const res = await fetch(path, { ...init, headers, cache: "no-store" });
  return res;
}

// Read a fetch Response body exactly once, returning JSON if it parses,
// otherwise the raw text. Reading twice — e.g. `res.json().catch(_ =>
// res.text())` — fails with "body stream already read" because both
// methods consume the body. Consume as text first, then JSON.parse.
export async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function getJson<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(path);
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `${path} returned ${res.status}`);
  }
  return (await res.json()) as T;
}
